export type AppType = 'cx' | 'cs';

/**
 * Deploy environments that emit telemetry. `dev` and `demo` are deliberately excluded.
 *
 * `import.meta.env.PROD` reflects the build mode, not the deployment target: both
 * apps' `build` script is plain `vite build` with no `--mode`, so there is one
 * artifact, built once in Vite's default "production" mode, deployed unchanged to
 * every target (prod, int, demo, dev) with per-target values injected afterward via
 * `.envs/*` (BL1). `DIST_ENV` is one of those injected values and is what actually
 * identifies the target, so use it here instead of `PROD`.
 */
export const GTM_EMITTING_ENVIRONMENTS = ['int', 'prod'] as const;

export function isEmittingEnvironment(distEnv: string | undefined): boolean {
  return !!distEnv && (GTM_EMITTING_ENVIRONMENTS as readonly string[]).includes(distEnv);
}

/**
 * Google's public tag-serving host, used when no first-party server container is
 * configured for this environment.
 *
 * @see https://developers.google.com/tag-platform/tag-manager/server-side/send-data
 */
export const DEFAULT_GTM_HOST = 'www.googletagmanager.com';

/** Runtime configuration for GTM injection. */
export interface GtmConfig {
  /**
   * First-party server-side GTM (sGTM) container origin + path for this environment
   * (e.g. `https://one.shi.com/analytics`, AEGIS-599). Optional: unset falls back to
   * {@link DEFAULT_GTM_HOST} with no behavior change.
   *
   * This only controls where the container *script* (`gtm.js`/`ns.html`) loads from —
   * setting it does **not** by itself route measurement hits through the server
   * container. That also requires `server_container_url` to be set on the GA4 tags
   * inside the GTM web container, which is a GTM-admin setting, not code. Both must be
   * true for a given tag's traffic to actually travel through sGTM.
   */
  serverContainerUrl?: string;
}